// src/components/admin/scheduling/components/MasterScheduleView.js
import React, { useMemo } from "react";
import DaySection from "./DaySection";
import { DAYS_ORDER, DAY_META, WEEKDAY_BLOCKS, SATURDAY_BLOCKS, SLOTS_PER_BLOCK } from "../constants";
import { addDays, clampDateToMidnight } from "../dateUtils";
import { defaultTimelineRangeForDayKey as defaultScheduleTimelineRangeForDayKey } from "../scheduleSettingsUtils";

function defaultTimelineRangeForDayKey(dayKey) {
  return defaultScheduleTimelineRangeForDayKey(dayKey);
}

function resolveTimelineRangeForDay(dayKey, config = {}) {
  const defaults = defaultTimelineRangeForDayKey(dayKey);
  const rawStart = Number(config?.timelineStartMin);
  const rawEnd = Number(config?.timelineEndMin);

  if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd) || rawEnd <= rawStart) {
    return defaults;
  }

  const startMin = Math.max(0, Math.min(23 * 60 + 55, rawStart));
  const endMin = Math.max(startMin + 60, Math.min(24 * 60, rawEnd));
  return { startMin, endMin };
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const asNum = Number(value);
  return Number.isFinite(asNum) ? asNum : 0;
}

function pickLatestAvailabilityRecord(records = []) {
  if (!Array.isArray(records) || records.length === 0) return null;
  return records.reduce((latest, rec) => {
    if (!latest) return rec;
    return timestampToMillis(rec?.updatedAt) > timestampToMillis(latest?.updatedAt) ? rec : latest;
  }, null);
}

export default function MasterScheduleView({
  viewMode,
  sortedTutors,
  selectedTutorUid,
  masterSessions,
  tutors,
  dayRefs,
  timeSlotLayout,
  selectedSessionId,
  selectedSlot,
  selectedSessionIdRef,
  selectedSlotRef,
  setSelectedSessionId,
  setSelectedSlot,
  moveSessionToSlot,
  onSessionResize,
  onSessionReorder,
  setCreateCtx,
  setEditSession,
  masterAvailability = [], // NEW PROP
  masterDayConfigs = {},   // NEW PROP
  onEditDay // Ensure this is passed
}) {
  const templateWeekStart = useMemo(() => clampDateToMidnight(new Date(2000, 0, 3)), []);

  const templateDays = useMemo(() => {
    return DAYS_ORDER.map((dayKey, idx) => ({
      dayKey,
      date: addDays(templateWeekStart, idx),
      meta: DAY_META[dayKey],
    }));
  }, [templateWeekStart]);

  const visibleDays = useMemo(() => {
    if (viewMode === "week") return templateDays;
    if (DAYS_ORDER.includes(viewMode)) return templateDays.filter((d) => d.dayKey === viewMode);
    return templateDays;
  }, [viewMode, templateDays]);

  const model = useMemo(() => {
    const byDay = {};
    for (const { dayKey, date, meta } of templateDays) {
      
      // 1. DETERMINE LAYOUT
      // Check if we have a saved config for this master day
      const dayConfig = masterDayConfigs[dayKey];
      let timeBlocks = dayKey === "saturday" ? SATURDAY_BLOCKS : WEEKDAY_BLOCKS;
      const { startMin: timelineStartMin, endMin: timelineEndMin } = resolveTimelineRangeForDay(dayKey, dayConfig);
      
      if (dayConfig && dayConfig.timeBlocks && dayConfig.timeBlocks.length > 0) {
        timeBlocks = dayConfig.timeBlocks;
      }
      
      const isClosed = dayConfig ? !!dayConfig.isClosed : false;

      const dayAvail = masterAvailability.filter(a => a.dayKey === dayKey);

      const tutorRows = [];

      // Sort tutors first
      const tutorList = selectedTutorUid
        ? sortedTutors.filter((t) => t.id === selectedTutorUid)
        : sortedTutors;

      for (const tutor of tutorList) {
        const tutorSessions = masterSessions.filter((s) => s.tutorUid === tutor.id && s.dayKey === dayKey);
        const hasSessions = tutorSessions.length > 0;

        const avRecord = pickLatestAvailabilityRecord(dayAvail.filter((d) => d.uid === tutor.id));
        const isMarkedUnavailable = !!avRecord?.unavailable;
        const isHidden = !!avRecord?.hidden;
        if (isHidden && !hasSessions) {
          continue;
        }

        const rawStart = Number(avRecord?.startMin);
        const rawEnd = Number(avRecord?.endMin);
        const avStart = Number.isFinite(rawStart) ? rawStart : 0;
        const avEnd = Number.isFinite(rawEnd) && rawEnd > avStart ? rawEnd : 24 * 60;
        const effectiveAvailability = isMarkedUnavailable ? [] : [{ startMin: avStart, endMin: avEnd }];
        const hasTimelineAvailability = effectiveAvailability.some(
          (seg) => seg.startMin < timelineEndMin && timelineStartMin < seg.endMin
        );

        // Build Blocks
        const blocksMap = {};

        for (const block of timeBlocks) {
          const overlaps = effectiveAvailability.some(
            (seg) => seg.startMin < block.endMin && block.startMin < seg.endMin
          );
          
          blocksMap[block.id] = {
            isAvailable: overlaps,
            sessionLists: Array.from({ length: SLOTS_PER_BLOCK }, () => []),
          };
        }

        for (const s of tutorSessions) {
          const blockId = s.blockId;
          const slotIdx = s.slotIdx;
          if (!blocksMap[blockId]) continue; 
          // ... populate sessionLists (same as before)
          const studentLabel = s.studentName && s.studentGrade != null 
             ? `${s.studentName} (${s.studentGrade})` 
             : s.studentName || "Student";
          blocksMap[blockId].sessionLists[slotIdx].push({ ...s, studentLabel });
        }

        if (hasTimelineAvailability || hasSessions) {
           tutorRows.push({
             tutor,
             blocks: blocksMap,
             isGhost: hasSessions && !hasTimelineAvailability,
             availabilities: effectiveAvailability,
           });
        }
      }

      byDay[dayKey] = {
        dayKey,
        date,
        meta,
        timeBlocks,
        tutorRows,
        isClosed, // Pass closed status
        timelineStartMin,
        timelineEndMin,
      };
    }
    return byDay;
  }, [templateDays, sortedTutors, selectedTutorUid, masterSessions, masterAvailability, masterDayConfigs]); // Add deps

  return (
    <>
      {visibleDays.map(({ dayKey }) => {
        const dayData = model[dayKey];
        if (!dayData) return null;

        return (
          <DaySection
            key={dayKey}
            dayKey={dayKey}
            date={dayData.date}
            timeBlocks={dayData.timeBlocks}
            timelineStartMin={dayData.timelineStartMin}
            timelineEndMin={dayData.timelineEndMin}
            tutorRows={dayData.tutorRows}
            isClosed={dayData.isClosed}
            pendingCount={0}
            confirmedCount={0}
            totalCount={0}
            headerDayOnly={true}
            onEditDay={() => onEditDay(dayKey)}
            // Create / edit
            onEmptySlotDoubleClick={(ctx) => {
              const tutor = tutors.find((t) => t.id === ctx.tutorUid);
              setCreateCtx({
                ...ctx,
                tutorName: tutor?.name || tutor?.email || "Tutor",
                dayKey,
              });
            }}
            onSessionDoubleClick={(s) => setEditSession(s)}
            // Selection + DnD
            selectedSessionId={selectedSessionId}
            selectedSlotKey={selectedSlot?.slotKey || null}
            onSessionClick={(s) => {
              selectedSessionIdRef.current = s.id;
              selectedSlotRef.current = null;
              setSelectedSessionId(s.id);
              setSelectedSlot(null);
            }}
            onEmptySlotClick={(slot) => {
              selectedSessionIdRef.current = null;
              selectedSlotRef.current = slot;
              setSelectedSlot(slot);
              setSelectedSessionId(null);
            }}
            onSessionDragStart={(e, s) => {
              e.dataTransfer.setData("text/plain", s.id);
              selectedSessionIdRef.current = s.id;
              selectedSlotRef.current = null;
              setSelectedSessionId(s.id);
              setSelectedSlot(null);
            }}
            onSlotDrop={async (draggedSessionId, slot) => {
              await moveSessionToSlot(draggedSessionId, slot);
            }}
            onSessionResize={onSessionResize}
            onSessionReorder={onSessionReorder}
            
            // --- CRITICAL: Pass null to disable dropdowns explicitly in SessionCard ---
            onChangeSessionStatus={null} 
            
            readOnly={false}
            timeSlotLayout={timeSlotLayout}
            
            dayRef={(el) => {
              if (dayRefs.current) dayRefs.current[dayKey] = el;
            }}
          />
        );
      })}
    </>
  );
}
